import React, {useEffect, useMemo, useState} from "react"
import {Claims, isOrgUser, useAccessToken, useUserType} from "../../services/auth"
import {getDefaultDatePreset, getInitialDatePreset, getDatePresets} from "./defaultDatePreset"
import {upperFirst} from "lodash"
import classNames from "classnames"
import {Cents, Direction, getTransactionsRequest, Transaction, TransactionTypes} from "../../resources/transaction"
import {usePaging} from "../../hooks/usePaging"
import SearchBox from "../SearchBox/SearchBox"
import {useQueryState} from "use-location-state"
import {ErrorDocument} from "../../resources/common"
import {downloadFile} from "../../utilities/file"
import {useToasts} from "react-toast-notifications"
import DatePickerWithPresets, {getPresetDateRangeIso} from "../DatePicker/DatePicker"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {Filter} from "../Filter/Filter"
import {startCase} from "lodash"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {Org} from "../../resources/org"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import {AllowedTransactionsColumns} from "./Transactions.types"
import TransactionsTable from "./TransactionsTable"

interface TransactionsProps {
	accountId?: string
	customerId?: string
	cardId?: string
	limit?: number
	isUsingPaging?: boolean
	enableSearch?: boolean
	enableDirectionFilter?: boolean
	enableOrgFilter?: boolean
	token?: string
	includedColumns: Array<AllowedTransactionsColumns>
	excludeGlTransaction?: boolean
	enableTitle?: boolean
	fullHeight?: boolean
	disableShadow?: boolean
	refreshToken?: number
	dataTableClassName?: string
	enableTypeFilter?: boolean
	enableAmountFilter?: boolean
	enableDateFilter?: boolean
	enableExport?: boolean
	title?: string
	claims?: Claims
	titleClassName?: string
	className?: string
	hideMinHeight?: boolean
	disabled?: boolean
	onSelectTransaction?: (tx: Transaction) => void
}

export function Transactions({
	limit = 10,
	isUsingPaging = true,
	enableSearch = true,
	enableDirectionFilter = false,
	enableOrgFilter = false,
	token,
	accountId,
	customerId,
	cardId,
	includedColumns,
	excludeGlTransaction = false,
	enableTitle = true,
	fullHeight = true,
	disableShadow = false,
	enableTypeFilter = true,
	enableAmountFilter = true,
	enableDateFilter = true,
	enableExport = true,
	refreshToken,
	dataTableClassName,
	title = "Transactions",
	claims,
	titleClassName,
	className,
	hideMinHeight,
	disabled,
	onSelectTransaction,
}: TransactionsProps) {
	const prefix = "tran"
	const userType = useUserType(claims)
	const isScopedView = customerId != null || accountId != null || cardId != null
	const defaultDatePreset = getDefaultDatePreset(isScopedView, isOrgUser(userType))
	const defaultDateRange = useMemo(() => getPresetDateRangeIso(defaultDatePreset), [defaultDatePreset])
	const accessToken = token ?? useAccessToken()
	const {addToast} = useToasts()
	const [query, setQuery] = useQueryState("query", "")
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Transactions", 500, onSearch)
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, defaultDateRange.since)
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, defaultDateRange.until)
	const [initialDatePreset] = useState(() => getInitialDatePreset(since, isScopedView, defaultDatePreset))
	const [filteredByOrg, setFilteredByOrg] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [filteredTransactions, setFilteredTransactions] = useQueryState<string[]>(`${prefix}-filter[type]`, [])
	const [directions, setDirections] = useQueryState<Direction[]>(`${prefix}-filter[direction]`, [])
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")
	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		getTransactions,
		(x) => x.data.length,
		[
			query,
			since,
			until,
			sortBy,
			[...filteredTransactions, ...directions, ...filteredByOrg].join(","),
			refreshToken,
			fromAmount,
			toAmount,
			cardId,
		],
		`${prefix}-`,
		true
	)

	const directionsOptions = new Map([Direction.Debit, Direction.Credit].map((v) => [v, v]))

	const isFiltering =
		filteredTransactions.length != 0 ||
		query != "" ||
		since != "" ||
		until != "" ||
		directions.length != 0 ||
		fromAmount != "" ||
		toAmount != ""

	const transactionTypesMap = new Map(
		Object.entries(TransactionTypes)
			.map((t) => [upperFirst(t[1].replace("Transaction", "")), startCase(t[0])] as [string, string])
			.sort((a, b) => a[1].localeCompare(b[1]))
	)

	const typeFilter = (
		<Filter
			onFilterFunc={() => reset(limit)}
			options={transactionTypesMap}
			setStatuses={setFilteredTransactions}
			statuses={filteredTransactions}
			title="All types"
		/>
	)

	const directionsFilter = (
		<Filter
			statuses={directions}
			setStatuses={setDirections}
			onFilterFunc={() => reset(limit)}
			options={directionsOptions}
			title="Direction"
		/>
	)

	const orgs =
		(userType === "unit" || userType === "bank") && enableOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredByOrg, filteredByOrg, reset, limit),
		[orgs, setFilteredByOrg, filteredByOrg.join(","), reset, limit]
	)

	const dateTime = (
		<DatePickerWithPresets
			defaultPreset={initialDatePreset}
			presets={getDatePresets(isScopedView)}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
				reset(limit)
			}}
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={(value) => {
				setFromAmount(value)
				reset(limit)
			}}
			to={toAmount}
			setTo={(value) => {
				setToAmount(value)
				reset(limit)
			}}
		/>
	)

	function getTransactions(offset: number, limit: number) {
		return getTransactionsRequest({
			accessToken,
			offset,
			limit,
			searchQuery: enableSearch ? query : "",
			since,
			until,
			excludeGlTransaction: excludeGlTransaction,
			types: filteredTransactions,
			accountId,
			customerId,
			cardId,
			include: accountId && customerId ? "" : "customer,org",
			requestType: "json",
			direction: directions,
			fromAmount,
			toAmount,
			orgs: filteredByOrg,
			sort: sortBy,
		})
	}

	async function exportTransactions() {
		const response = await getTransactionsRequest({
			accessToken,
			offset: 0,
			searchQuery: enableSearch ? query : "",
			since,
			until,
			excludeGlTransaction: excludeGlTransaction,
			types: filteredTransactions,
			accountId,
			customerId,
			cardId,
			requestType: "csv",
			direction: directions,
			fromAmount,
			toAmount,
			orgs: filteredByOrg,
			include: "org",
			sort: sortBy,
		})

		response.match(
			(d) => downloadFile("Transactions.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)

		return true
	}

	useEffect(() => {
		let isActive = true
		if (isActive) {
			result.match(
				() => null,
				() => setIsSearchLoading(false),
				() => setIsSearchLoading(false)
			)
		}
		return () => {
			isActive = false
		}
	}, [result])

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	function toggleSort() {
		setSortBy(sortBy == "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	return (
		<DataTableCard
			className={classNames(
				"transactions-card",
				disableShadow && "transactions-card-disable-shadow",
				dataTableClassName
			)}
			cardClassName={className}
		>
			<DataTableActionHeader
				searchBox={enableSearch ? searchBox : null}
				dateTimePicker={enableDateFilter ? dateTime : null}
				filters={[
					...(enableOrgFilter ? [orgFilter] : []),
					...(enableTypeFilter ? [typeFilter] : []),
					...(enableAmountFilter ? [amountFilter] : []),
					...(enableDirectionFilter ? [directionsFilter] : []),
				]}
				exportFunc={enableExport ? exportTransactions : undefined}
				title={enableTitle ? title : null}
				enableSticky={fullHeight}
				titleClassName={classNames(titleClassName, disabled && "disabled")}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: transactions}) => {
					return (
						<TransactionsTable
							fullHeight={fullHeight}
							transactions={transactions.data}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={isUsingPaging}
							include={transactions.included}
							includedColumns={includedColumns}
							sortFunction={toggleSort}
							sortBy={sortBy}
							isFiltering={isFiltering}
							isDateRangeLimited={!isScopedView && since !== ""}
							hideMinHeight={hideMinHeight}
							disabled={disabled}
							onSelect={onSelectTransaction}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
